import type { TableColumnProp } from '@lmc/ui-kit'
import qs from 'qs'
import * as React from 'react'
import { Link } from 'react-router-dom'
import type { Data, TableData } from '../../CareDeliveryScreen.types'
import type { CareDeliveryTableProps } from './CareDeliveryTable'

const renderCountCell = (count: number, logsDashboardQuery?: string) => {
  if (!count) return '-'
  if (logsDashboardQuery)
    return (
      <Link to={`/logs?${logsDashboardQuery}`}>
        <span className="care-delivery-link">{count}</span>
      </Link>
    )
  return <span className="care-delivery-empty">{count}</span>
}

const renderTotal = (row: number, query: string) =>
  row ? (
    <Link to={`/logs?${query}`}>
      <span className="care-delivery-link care-delivery-total">{row}</span>
    </Link>
  ) : (
    '-'
  )

type GetTableColumnsParams = Pick<
  CareDeliveryTableProps,
  | 'items'
  | 'to'
  | 'from'
  | 'categories'
  | 'categoryId'
  | 'preferredPeopleNominative'
>

export const getTableColumns = ({
  items,
  to,
  from,
  categories,
  categoryId,
  preferredPeopleNominative
}: GetTableColumnsParams): TableColumnProp[] => {
  let columns: TableColumnProp[]

  const columnDefaults: Pick<
    TableColumnProp,
    'align' | 'sortable' | 'cellType' | 'width' | 'defaultSortOrder'
  > = {
    cellType: 'custom',
    sortable: true,
    width: 120,
    align: 'right',
    defaultSortOrder: null
  }

  const leftFixedColumns: TableColumnProp[] = [
    {
      key: 'resident',
      title: preferredPeopleNominative,
      cellType: 'resident',
      fixed: 'left',
      width: 180,
      sortable: true,
      sortKey: 'residentSortKey'
    },
    {
      key: 'total',
      title: 'Total',
      cellType: 'custom',
      fixed: 'left',
      width: 80,
      sortable: true,
      sortKey: 'totalSortKey',
      align: 'right',
      defaultSortOrder: null,
      renderCell: (row, data: { resident?: { resident: { id: string } } }) =>
        renderTotal(
          row as number,
          qs.stringify({
            resident: data?.resident?.resident?.id,
            category: categoryId,
            to: to.format('DD/MM/YYYY'),
            from: from.format('DD/MM/YYYY')
          })
        ) as JSX.Element
    }
  ]

  if (categoryId) {
    // Display one column per log category item
    const itemColumns: TableColumnProp[] = items
      .filter((item: any) => item.category === categoryId)
      .map((item: any) => ({
        ...columnDefaults,
        title: item.name,
        key: item.id,
        sortKey: `${item.id}Original`,
        renderCell: (row, data) =>
          renderCountCell(
            row as number,
            qs.stringify({
              // @ts-ignore
              resident: data?.resident?.resident?.id,
              item: item.id,
              to: to.format('DD/MM/YYYY'),
              from: from.format('DD/MM/YYYY')
            })
          ) as JSX.Element,
        defaultSortOrder: null
      }))

    columns = [
      ...leftFixedColumns,
      ...itemColumns,
      {
        ...columnDefaults,
        title: 'Custom',
        key: 'custom',
        sortKey: `customOriginal`,
        renderCell: (count) => renderCountCell(count as number) as JSX.Element
      }
    ]
  } else {
    // Display one column per log category
    const categoryColumns: TableColumnProp[] = categories.map((cat: any) => ({
      ...columnDefaults,
      title: cat.name,
      key: cat.id,
      sortKey: `${cat.id}Original`, // Used for sorting
      renderCell: (row, data) =>
        renderCountCell(
          row as number,
          qs.stringify({
            // @ts-ignore
            resident: data?.resident?.resident?.id,
            category: cat.id,
            to: to.format('DD/MM/YYYY'),
            from: from.format('DD/MM/YYYY')
          })
        ) as JSX.Element
    }))

    columns = [...leftFixedColumns, ...categoryColumns]
  }

  return columns
}

export const formatTableData = (data: Data[]): TableData[] =>
  data.map((row) => ({
    ...row,
    // flatten data object from [{ id, count }, ... ] to { id1: count1, id2: count2, ... }
    ...row.data.reduce(
      (accumulator, { id, count }) => ({
        ...accumulator,
        [`${id}Original`]: count,
        [id]: count
      }),
      {}
    ),
    totalSortKey: row.total,
    residentSortKey: row.resident.name.first + row.resident.name.last,
    resident: {
      avatarSize: 32,
      resident: row.resident
    }
  }))
